import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DollarSign, FileText, Users, Unlock } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";

const STATUS_LABEL = {
  locked: "Locked",
  unlocked_one_off: "One-off",
  unlocked_subscription: "Unlimited",
  unlocked: "Unlocked",
  sample: "Sample",
};

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch (_) { return iso.slice(0, 16).replace("T", " "); }
}

const METRICS = [
  { key: "revenue_usd",         label: "Total revenue",     icon: DollarSign, format: (v) => `$${(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
  { key: "reports_generated",   label: "Reports generated", icon: FileText,   format: (v) => String(v ?? 0) },
  { key: "reports_unlocked",    label: "Reports unlocked",  icon: Unlock,     format: (v) => String(v ?? 0) },
  { key: "active_subscribers",  label: "Active subscribers",icon: Users,      format: (v) => String(v ?? 0) },
];

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/stats");
        setStats(data);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load admin stats.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <>
      <Header variant="app" />
      <section className="max-w-6xl mx-auto px-6 py-10 md:py-14">
        <span className="text-xs font-semibold uppercase tracking-widest text-brand-blue">Admin</span>
        <h1
          data-testid="admin-headline"
          className="mt-2 font-heading text-3xl md:text-4xl font-bold tracking-tight text-ink-900"
        >
          Business dashboard
        </h1>

        {/* Metric cards */}
        <div
          data-testid="admin-metric-cards"
          className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {METRICS.map((m) => {
            const Icon = m.icon;
            const value = stats?.totals?.[m.key];
            return (
              <div
                key={m.key}
                data-testid={`admin-metric-${m.key}`}
                className="rounded-2xl bg-white border border-slate-100 p-5 shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-widest text-ink-500">
                    {m.label}
                  </span>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-blue-light text-brand-blue">
                    <Icon className="h-4 w-4" strokeWidth={2.2} />
                  </span>
                </div>
                <p className="mt-3 font-heading text-2xl md:text-3xl font-bold text-ink-900">
                  {loading ? <span className="inline-block h-7 w-16 rounded bg-slate-100 animate-pulse" /> : m.format(value)}
                </p>
              </div>
            );
          })}
        </div>

        {/* Recent reports */}
        <div className="mt-10">
          <h2 className="font-heading text-xl font-semibold text-ink-900">Recent report generations</h2>
          <div className="mt-4 rounded-2xl bg-white border border-slate-100 overflow-hidden">
            <table data-testid="admin-recent-reports" className="w-full text-sm">
              <thead className="bg-surface-subtle text-ink-500">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold uppercase text-xs tracking-wider">Date</th>
                  <th className="text-left px-4 py-3 font-semibold uppercase text-xs tracking-wider">Symptoms</th>
                  <th className="text-left px-4 py-3 font-semibold uppercase text-xs tracking-wider">Categories</th>
                  <th className="text-left px-4 py-3 font-semibold uppercase text-xs tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 font-semibold uppercase text-xs tracking-wider">Email</th>
                </tr>
              </thead>
              <tbody>
                {(stats?.recent_reports || []).map((r) => (
                  <tr key={r.report_id} className="border-t border-slate-100">
                    <td className="px-4 py-3 text-ink-500 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-3 text-ink-900 max-w-xs truncate" title={r.symptoms}>{r.symptoms}</td>
                    <td className="px-4 py-3 text-ink-500">
                      <div className="flex flex-wrap gap-1">
                        {r.categories.map((c) => (
                          <span key={c} className="text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 bg-slate-50 text-ink-600 border border-slate-100">
                            {c}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-ink-500">{STATUS_LABEL[r.status] || r.status}</td>
                    <td className="px-4 py-3 text-ink-500 font-mono text-xs">{r.email_masked || "—"}</td>
                  </tr>
                ))}
                {!loading && (stats?.recent_reports || []).length === 0 && (
                  <tr><td colSpan={5} className="text-center text-ink-500 py-8">No reports yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent purchases */}
        <div className="mt-10">
          <h2 className="font-heading text-xl font-semibold text-ink-900">Recent purchases</h2>
          <div className="mt-4 rounded-2xl bg-white border border-slate-100 overflow-hidden">
            <table data-testid="admin-recent-purchases" className="w-full text-sm">
              <thead className="bg-surface-subtle text-ink-500">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold uppercase text-xs tracking-wider">Date</th>
                  <th className="text-left px-4 py-3 font-semibold uppercase text-xs tracking-wider">Plan</th>
                  <th className="text-left px-4 py-3 font-semibold uppercase text-xs tracking-wider">Amount</th>
                  <th className="text-left px-4 py-3 font-semibold uppercase text-xs tracking-wider">Email</th>
                </tr>
              </thead>
              <tbody>
                {(stats?.recent_purchases || []).map((p, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-4 py-3 text-ink-500 whitespace-nowrap">{fmtDate(p.paid_at)}</td>
                    <td className="px-4 py-3 text-ink-900 capitalize">{(p.plan || "").replace("_", " ")}</td>
                    <td className="px-4 py-3 text-ink-900 font-medium">
                      ${(p.amount_usd ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 text-ink-500 font-mono text-xs">{p.email_masked || "—"}</td>
                  </tr>
                ))}
                {!loading && (stats?.recent_purchases || []).length === 0 && (
                  <tr><td colSpan={4} className="text-center text-ink-500 py-8">No purchases yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}
